import React, {useState} from "react";
import {Row} from "reactstrap";
import {Divider} from "@mui/material";
import Button from "../../../../../base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import ApiResponseCodes from "../../../../../../../core/models/static/api-response-codes";
import AddPartSupportRequestLookupSectionRenewalExpirationDateSection from "./sections/expiration-renewal";
import AddPartSupportRequestLookupSectionInvoicesSection from "./sections/invoices";
import AddPartSupportRequestLookupSectionBaseSection from "./sections/base";
import AddPartSupportRequestLookupSectionWarehousesSection from "./sections/warehouses";
import {AddPartSupportRequestProcessSteps} from "../../index";


const AddPartSupportRequestLookupSection = ({
                                                defineValues,
                                                lookupValues,
                                                setLookupValues,
                                                setPartInfoValues,
                                                setProcess,
                                                terminateProcess,
                                            }) => {
    const [submitting, setSubmitting] = useState(false);
    const [showExpirationRenewal, setShowExpirationRenewal] = useState({show: false, message: ''});
    const isMounted = useIsMounted();

    const disabled =
        !!(!lookupValues.showProductOrSerialNoSelection && !lookupValues.serialNumber?.length && !lookupValues?.part) || (
            lookupValues.showProductOrSerialNoSelection && (
                (!lookupValues.serialNumber?.length) &&
                (!lookupValues.selectedQuantity ||
                    Number.isNaN(Number(lookupValues.selectedQuantity)) ||
                    parseInt(lookupValues.selectedQuantity) <= 0)
            )
        );


    /**
     * Sends an api request for an initial submission of the part support request
     *
     * * if the result of the api is successful, navigates the operator to the part information section of the dialog.
     * * if the api errors with a deduplicate rga, navigates the operator back to define section
     * * if the api errors with an invalidSerialNumber, does nothing since the current section has errored.
     * * if the api errors with an expiredPartSupport, shows the expiration date section.
     *
     * @param {boolean} quickRGA    whether this submission is via the QUICK_RGA method.
     */
    const submitInitialRequest = async (quickRGA = false) => {
        setSubmitting(true);
        const forApi = prepareInitialRequestDs();
        const response = await Api.submitInitialPartSupportRequest(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            if (quickRGA) {
                return terminateProcess(response.data?.request);
            }
            setPartInfoValues(prevState => ({
                ...prevState,
                request: response.data?.request ?? {},
                extraFees: response.data?.extraFees ?? [],
                serialNo: lookupValues.serialNumber,
            }));
            return setProcess(AddPartSupportRequestProcessSteps.partInfo);
        }
        switch (response?.errorCode) {
            // invalid RGA
            case ApiResponseCodes.duplicateRGA:
                setProcess(AddPartSupportRequestProcessSteps.define);
                break;
            // invalid Serial number
            case ApiResponseCodes.invalidSerialNumber:
                break;
            // expired date
            case ApiResponseCodes.expiredPartSupport:
                setShowExpirationRenewal({show: true, message: response.message});
                break;
            case 500:
            default:
                break;
        }
        setSubmitting(false);
    }

    /**
     * Prepares the Data structure of the new part support request.
     */
    const prepareInitialRequestDs = () => {
        const forApi = {
            customerId: defineValues?.customer?.id ?? undefined,
            serialNo: lookupValues?.serialNumber ?? undefined,
            partNo: lookupValues?.part?.partNumber ?? undefined,
            unserializableItemsCount: lookupValues?.serialNumber
                ? undefined
                : (typeof lookupValues?.selectedQuantity === "undefined" || lookupValues?.selectedQuantity === null)
                    ? undefined
                    : Number(lookupValues?.selectedQuantity),
            invoiceNo: lookupValues?.invoiceNo ?? undefined,
            typeID: defineValues.type?.id,
        }
        if (defineValues.manualRga) {
            forApi['RGA'] = defineValues.rga;
        }
        if (defineValues.inventoryBased) {
            forApi['warehouseCode'] = defineValues.warehouse?.code ?? undefined;
        }
        return forApi;
    }


    /**
     * If in the invoices' selection, then navigates back to the part / serial number section and nullifies
     * invoiceSerialNumber
     * else if in part / serial number section, nullifies both part and serialNumber and then redirects to define tab
     */
    const previousSection = () => {
        if (lookupValues.showProductOrSerialNoSelection) {
            return setLookupValues(prevState => ({
                ...prevState,
                showProductOrSerialNoSelection: false,
                serialNumber: null,
                selectedQuantity: null,
                invoiceNo: null,
                isSerialized: false,
            }));
        }
        if (!lookupValues.showProductOrSerialNoSelection) {
            setLookupValues(prevState => ({
                ...prevState,
                part: null,
                serialNumber: null,
                isSerialized: false,
            }));
            return setProcess(AddPartSupportRequestProcessSteps.define);
        }
    }

    /**
     * If a part was selected and the customer was not yet in invoices table, then shows the invoices
     * else if the part was selected and the customer also had selected their serial number, then submits the initial
     * request
     * else if the serialNumber was a valid non-empty string calls the submit initial request
     */
    const nextSection = () => {
        if (lookupValues.part) {
            if (!lookupValues.showProductOrSerialNoSelection)
                return setLookupValues(prevState => ({
                    ...prevState,
                    showProductOrSerialNoSelection: true,
                }));
            if (lookupValues.serialNumber)
                return submitInitialRequest();
            if (lookupValues.selectedQuantity && parseInt(lookupValues.selectedQuantity) > 0)
                return submitInitialRequest();
        }
        if (lookupValues.serialNumber?.length)
            return submitInitialRequest();
    }

    return (
        <Row className='no-gutters justify-content-center align-items-start w-100'>
            <div className={'w-100 row no-gutters'}>
                {
                    showExpirationRenewal?.show &&
                    <AddPartSupportRequestLookupSectionRenewalExpirationDateSection
                        message={showExpirationRenewal?.message}
                        defineValues={defineValues}
                        lookupValues={lookupValues}
                        submittingRequest={submitting}
                        submitInitialRequest={submitInitialRequest}
                        goBack={() => setShowExpirationRenewal({show: false, message: ''})}
                    />
                }
                {
                    !showExpirationRenewal?.show &&
                    <>
                        {
                            !lookupValues.showProductOrSerialNoSelection &&
                            <AddPartSupportRequestLookupSectionBaseSection
                                defineValues={defineValues}
                                lookupValues={lookupValues}
                                setLookupValues={setLookupValues}
                                submitInitialRequest={submitInitialRequest}
                            />
                        }
                        {
                            lookupValues.showProductOrSerialNoSelection && (
                                <>
                                    {
                                        !!defineValues.customer &&
                                        <AddPartSupportRequestLookupSectionInvoicesSection
                                            defineValues={defineValues}
                                            lookupValues={lookupValues}
                                            setLookupValues={setLookupValues}
                                            previousSection={previousSection}
                                        />
                                    }
                                    {
                                        !!defineValues.warehouse &&
                                        <AddPartSupportRequestLookupSectionWarehousesSection
                                            defineValues={defineValues}
                                            lookupValues={lookupValues}
                                            setLookupValues={setLookupValues}
                                            previousSection={previousSection}
                                        />
                                    }
                                </>
                            )
                        }
                    </>
                }
            </div>
            {
                !showExpirationRenewal?.show &&
                <>
                    <div className={'w-100 my-4'}/>
                    <Divider className={'divider'}/>
                    <Row className='no-gutters footer justify-content-between align-items-start w-100'>
                        <Button
                            disabled={submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={previousSection}
                            borderRadius={'0 8px 0 8px'}
                        >
                            {submitting ? 'submitting...' : 'Back'}
                        </Button>
                        <Button
                            disabled={disabled || submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={nextSection}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {submitting ? 'submitting...' : 'Next'}
                        </Button>
                    </Row>
                </>
            }
        </Row>
    );
}


export default AddPartSupportRequestLookupSection;
